import { buildToothPalette } from '../../utilities/toothShading';

const SVG_NS = 'http://www.w3.org/2000/svg';

const UPPER_ARCH_CURVE = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const LOWER_ARCH_CURVE = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

/** Shade used for selected teeth when the formula line has no color yet. */
const NATURAL_SHADE = '#f4eee2';
/** Unselected teeth stay muted so the chosen shade stands out. */
const IDLE_SHADE = '#b3aa9e';

const LABEL_COLOR = '#f1d9da';
const LABEL_HOVER_COLOR = '#ffffff';
const HOVER_STROKE = '#7fe3e5';

const svgEl = (name, attrs = {}, children = []) => {
    const node = document.createElementNS(SVG_NS, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
    children.forEach((child) => node.appendChild(child));
    return node;
};

const gradientStops = (stops) => stops.map(([offset, color, opacity = 1]) => svgEl('stop', {
    offset,
    'stop-color': color,
    'stop-opacity': opacity,
}));

const enamelGradient = (id, palette) => svgEl('radialGradient', { id, cx: '40%', cy: '34%', r: '72%' }, gradientStops([
    ['0%', palette.highlight],
    ['35%', palette.light],
    ['68%', palette.base],
    ['92%', palette.shade],
    ['100%', palette.edge],
]));

const blurFilter = (id, stdDeviation) => svgEl('filter', { id, x: '-20%', y: '-20%', width: '140%', height: '140%' }, [
    svgEl('feGaussianBlur', { stdDeviation }),
]);

/** Catmull-Rom spline through the points, as cubic Bézier path data. */
const smoothPath = (points) => {
    if (points.length < 2) {
        return '';
    }
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i += 1) {
        const p0 = points[i - 1] || points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
    }
    return d;
};

const toothCenter = (tooth) => {
    const box = tooth.getBBox();
    return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
};

/**
 * Restyles a Raphael tooth chart to look like teeth in a mouth: dark oral background,
 * gum arches, enamel shading, and selected teeth rendered in `color`.
 */
export const applyRealisticSkin = (paper, { teeth, labels, color, idPrefix, width, height }) => {
    const svg = paper.canvas;
    const defs = paper.defs;
    const prefix = String(idPrefix).replace(/[^\w-]/g, '_');
    const ids = {
        mouth: `${prefix}-rs-mouth`,
        selected: `${prefix}-rs-enamel-selected`,
        idle: `${prefix}-rs-enamel-idle`,
        shadow: `${prefix}-rs-shadow`,
        gumBlur: `${prefix}-rs-gum-blur`,
        gumSoft: `${prefix}-rs-gum-soft`,
    };
    const palettes = {
        selected: buildToothPalette(color || NATURAL_SHADE),
        idle: buildToothPalette(IDLE_SHADE),
    };

    defs.appendChild(svgEl('radialGradient', { id: ids.mouth, cx: '50%', cy: '50%', r: '70%' }, gradientStops([
        ['0%', '#7a2d35'],
        ['55%', '#4d161c'],
        ['100%', '#1e0709'],
    ])));
    defs.appendChild(enamelGradient(ids.selected, palettes.selected));
    defs.appendChild(enamelGradient(ids.idle, palettes.idle));
    defs.appendChild(svgEl('filter', { id: ids.shadow, x: '-30%', y: '-30%', width: '160%', height: '160%' }, [
        svgEl('feDropShadow', {
            dx: 0.6,
            dy: 1.2,
            stdDeviation: 1.1,
            'flood-color': '#1a0508',
            'flood-opacity': 0.65,
        }),
    ]));
    defs.appendChild(blurFilter(ids.gumBlur, 3));
    defs.appendChild(blurFilter(ids.gumSoft, 1.6));

    const toothNodes = new Map();
    Object.entries(teeth).forEach(([num, tooth]) => {
        if (tooth?.node) {
            toothNodes.set(tooth.node, Number(num));
        }
    });

    // Groove overlays are drawn right after their tooth, so DOM order maps them back.
    const grooves = {};
    let currentTooth = null;
    Array.from(svg.querySelectorAll('path')).forEach((node) => {
        if (toothNodes.has(node)) {
            currentTooth = toothNodes.get(node);
            grooves[currentTooth] = [];
            return;
        }
        if (currentTooth != null) {
            grooves[currentTooth].push(node);
        }
    });

    const background = svgEl('g', { 'pointer-events': 'none' });
    background.appendChild(svgEl('rect', { x: 0, y: 0, width, height, fill: `url(#${ids.mouth})` }));
    [UPPER_ARCH_CURVE, LOWER_ARCH_CURVE].forEach((arch) => {
        const d = smoothPath(arch.filter((num) => teeth[num]).map((num) => toothCenter(teeth[num])));
        const stroke = (attrs) => background.appendChild(svgEl('path', {
            d,
            fill: 'none',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
            ...attrs,
        }));
        stroke({ stroke: '#8e3440', 'stroke-width': 36, opacity: 0.9, filter: `url(#${ids.gumBlur})` });
        stroke({ stroke: '#cf6f79', 'stroke-width': 29, filter: `url(#${ids.gumSoft})` });
        stroke({ stroke: '#e6949a', 'stroke-width': 20, opacity: 0.8, filter: `url(#${ids.gumSoft})` });
        stroke({ stroke: '#f7c6c4', 'stroke-width': 6, opacity: 0.25, filter: `url(#${ids.gumSoft})` });
    });
    svg.insertBefore(background, defs.nextSibling);

    Object.values(labels).forEach((label) => {
        label?.attr?.({ fill: LABEL_COLOR });
    });

    const selectedTeeth = new Set();

    const paint = (num, isSelected) => {
        const tooth = teeth[num];
        if (!tooth?.node) {
            return;
        }
        const palette = isSelected ? palettes.selected : palettes.idle;
        if (isSelected) {
            selectedTeeth.add(num);
        } else {
            selectedTeeth.delete(num);
        }
        tooth.node.setAttribute('fill', `url(#${isSelected ? ids.selected : ids.idle})`);
        tooth.node.setAttribute('stroke', palette.edge);
        tooth.node.setAttribute('stroke-width', '0.8');
        tooth.node.setAttribute('filter', `url(#${ids.shadow})`);
        (grooves[num] || []).forEach((node) => {
            node.setAttribute('stroke', palette.groove);
            node.setAttribute('stroke-opacity', '0.55');
            node.setAttribute('stroke-width', '0.55');
            node.setAttribute('stroke-linecap', 'round');
            node.setAttribute('stroke-linejoin', 'round');
            node.setAttribute('fill', 'none');
        });
    };

    const hover = (tooth, isHovering) => {
        const num = tooth?.node ? toothNodes.get(tooth.node) : undefined;
        if (num == null) {
            return;
        }
        const palette = selectedTeeth.has(num) ? palettes.selected : palettes.idle;
        tooth.node.setAttribute('stroke', isHovering ? HOVER_STROKE : palette.edge);
        tooth.node.setAttribute('stroke-width', isHovering ? '1.6' : '0.8');
        labels[num]?.attr?.({
            fill: isHovering ? LABEL_HOVER_COLOR : LABEL_COLOR,
            'font-weight': isHovering || selectedTeeth.has(num) ? 'bold' : 'normal',
        });
    };

    Object.keys(teeth).forEach((num) => paint(Number(num), false));

    return { paint, hover };
};
